import { utils, writeFile } from 'xlsx';
import { uniq } from 'lodash';
import { DATE_FORMAT } from './constants';
import { getWeekRange } from './getDate';
import { fDate } from './formatTime';

export const setLocalStorage = (key, value) => {
  localStorage.setItem(key, value);
};

export const getLocalStorage = (key) => {
  return localStorage.getItem(key);
};

export const stringify = (str) => {
  return JSON.stringify(str);
};

export const parseStrObj = (value, defaultValue = null) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    // If parsing fails, return the original string
    return value ?? defaultValue;
  }
};

export const parse = (str) => {
  return JSON.parse(str);
};

export const findById = (id, array) => {
  return array.find((obj) => obj.id === id);
};

export const percentage = (upper, lower) => {
  const upperValue = parseInt(upper, 10);
  const lowerValue = parseInt(lower, 10);
  if (upperValue > 0 && lowerValue > 0) {
    return Math.round((upperValue / lowerValue) * 100);
  }
  return 0;
};

export const uniqueArr = (arr) => uniq(arr);

export const buildSalesforceParamsQuery = (query, object) => {
  if (!object) return null;

  const localQuery = query ?? {};
  const { isShare, accountOrgId, tenantId } = object;

  if (isShare) {
    localQuery.isShare = true;
    if (accountOrgId) {
      localQuery.accountOrgId = accountOrgId;
    } else {
      localQuery.tenantId = tenantId;
    }
  }

  return localQuery;
};

export const editDataCommon = (params, recordData, crudOperations, featureManager = []) => {
  const editData = {
    id: params.row.feature_id,
    name: params.row.feature_name,
  };

  const processCustomObject = (obj) => {
    const operationType = JSON.parse(obj.operation_type);
    const allEvent = Array.isArray(operationType)
      && crudOperations.every((curd) => operationType.includes(curd.value));
    return { operation_type: operationType, allEvent };
  };

  // Add custom entity details
  (recordData?.customEntity || []).forEach((obj) => {
    let data = obj;
    if (obj.custom_entity_type === 'CustomObject') {
      data = { ...data, ...processCustomObject(obj) };
    }
    if (!editData[obj.custom_entity_type]) {
      editData[obj.custom_entity_type] = { data: [], ids: [] };
    }
    editData[obj.custom_entity_type].data.push(data);
    editData[obj.custom_entity_type].ids.push(obj.custom_entity_id);
  });

  // Add nested feature details
  if (featureManager && recordData?.nestedFeatures?.length) {
    const type = 'NestedFeature';
    let idKey = 'featureId';
    let nameKey = 'featureName';
    let array = featureManager;

    if (featureManager.featureManagerList?.length) {
      idKey = 'feature_id';
      nameKey = 'feature_name';
      array = featureManager.featureManagerList;
    }

    array.forEach((obj) => {
      if (recordData.nestedFeatures.some(
        (data) => data.nested_feature_id === obj[idKey],
      )) {
        if (!editData[type]) {
          editData[type] = { ids: [], data: [] };
        }
        editData[type].data.push({
          nested_feature_name: obj[nameKey],
          nested_feature_id: obj[idKey],
        });
        editData[type].ids.push(obj[idKey]);
      }
    });
  }

  return editData;
};

export const modifiedDateString = (date, type = 'default') => {
  const formatMap = {
    month: DATE_FORMAT.MONTH_DATE_GRAPH,
    week: DATE_FORMAT.WEEK_DATE_GRAPH,
    day: DATE_FORMAT.DAY_DATE_GRAPH,
    default: DATE_FORMAT.DAY_DATE,
  };
  const formattedDate = type === 'week' ? getWeekRange(date) : fDate(date, formatMap[type]);
  return formattedDate;
};

export const exportData = (fileName, data) => {
  const modifiedFileName = `${fileName}.xlsx`;
  // Convert accountsList to worksheet
  const ws = utils.json_to_sheet(data);

  // Create a workbook
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, fileName);

  // Save the workbook to a file
  writeFile(wb, modifiedFileName);
};

export const getUserRole = (role = 'user') => {
  const userRoles = {
    superAdmin: { level: 1, access: 'superAdmin', role: 'Super Admin' },
    admin: { level: 2, access: 'admin', role: 'Admin' },
    user: { level: 3, access: 'user', role: 'User' },
  };

  return role in userRoles ? userRoles[role] : userRoles.user;
};
